import { END_POINTS } from "../api/EndPoints";
import fetcher from "../api/fetcher";
import { PATH_BANK, PATH_DASHBOARD, PATH_KYC } from "../routes/paths";
import dayjs from "dayjs";
import { io, Socket } from "socket.io-client";
import { showToast } from "./Toast";

export function hideMobileNumber(number: string) {
  return number.slice(0, 2) + "*".repeat(number.length - 4) + number.slice(-2);
}

export function customSnakeCase(str: string) {
  return str.toLowerCase().replaceAll(" ", "_");
}

export function getLocation() {
  if ("geolocation" in navigator) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log("Latitude:", position.coords.latitude);
        console.log("Longitude:", position.coords.longitude);
      },
      (error) => {
        console.error("Error getting location:", error.message);
      },
    );
  } else {
    console.error("Geolocation is not supported by this browser.");
  }
}

export const NavigationSteps = [
  { status: [0, 1], url: PATH_KYC.businessVerification },
  { status: [2], url: PATH_KYC.businessDocuments },
  { status: [3, 4], url: PATH_KYC.directorAndPromoter },
  { status: [5], url: PATH_KYC.DirectorDocuments },
  { status: [6, 7, 8], url: PATH_KYC.ApplicationReview },
  { status: [9], url: PATH_BANK.BankSelection },
  { status: [10], url: PATH_BANK.WithdrawalBankAccount },
  { status: [11], url: PATH_DASHBOARD.home.root },
];
export const TabsData = [
  {
    label: "Business Verification",
    url: "/kyc/businessVerification",
    value: 0,
  },
  { label: "Business Documents", url: "/kyc/businessDocuments", value: 2 },
  {
    label: "Director / Promoter(s)",
    url: "/kyc/directorAndPromoter",
    value: 3,
  },
  { label: "Director Document", url: "/kyc/DirectorDocuments", value: 5 },
  { label: "Application review", url: "/kyc/applicationReview", value: 6 },
];

export const RouteAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: {
    type: "spring",
    stiffness: 100,
    damping: 12,
  },
  transitionTime: 300,
};

export const getSignedUrl = async (url: string) => {
  try {
    const response = await fetcher.get(END_POINTS.AUTH.SIGN_S3_URL(url));
    return response?.data?.preSignedUrl;
  } catch (error) {}
};

export const modalsSyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1.5,
  p: 4,
};

export const getCurrentLocation = (): Promise<{
  latitude: string;
  longitude: string;
}> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      showToast.error("Geolocation is not supported by this browser.");
      return reject(new Error("Geolocation is not supported by this browser."));
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude.toString();
        const longitude = position.coords.longitude.toString();

        resolve({ latitude, longitude });
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            showToast.error("Permission denied. Please allow location access.");
            reject(
              new Error("Permission denied. Please allow location access."),
            );
            break;
          case error.POSITION_UNAVAILABLE:
            showToast.error("Location unavailable. Try again later.");
            reject(new Error("Location unavailable. Try again later."));
            break;
          case error.TIMEOUT:
            showToast.error("Location request timed out.");
            reject(new Error("Location request timed out."));
            break;
          default:
            showToast.error(
              "An unknown error occurred while fetching location.",
            );
            reject(
              new Error("An unknown error occurred while fetching location."),
            );
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  });
};

export function numberToWords(amount: number) {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const numToWords = (num: number): string => {
    if (num < 20) return ones[num];
    if (num < 100)
      return (
        tens[Math.floor(num / 10)] + (num % 10 ? " " + ones[num % 10] : "")
      );
    if (num < 1000)
      return (
        ones[Math.floor(num / 100)] +
        " Hundred" +
        (num % 100 ? " and " + numToWords(num % 100) : "")
      );
    if (num < 100000)
      return (
        numToWords(Math.floor(num / 1000)) +
        " Thousand" +
        (num % 1000 ? " " + numToWords(num % 1000) : "")
      );
    if (num < 10000000)
      return (
        numToWords(Math.floor(num / 100000)) +
        " Lakh" +
        (num % 100000 ? " " + numToWords(num % 100000) : "")
      );
    return (
      numToWords(Math.floor(num / 10000000)) +
      " Crore" +
      (num % 10000000 ? " " + numToWords(num % 10000000) : "")
    );
  };

  const [rupeesPart, paisePart] = parseFloat(amount + "")
    .toFixed(2)
    .split(".");

  let words = "";
  if (+rupeesPart > 0) {
    words += numToWords(+rupeesPart) + " Rupees";
  }

  if (+paisePart > 0) {
    words += (words ? " and " : "") + numToWords(+paisePart) + " Paise";
  }

  return words || "Zero Rupees";
}

export const getDaysBetweenDates = (startDate: string, endDate: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, "day") + 1;
};

export function maskAadhaarNumber(input: string) {
  // Remove non-digit and non-* characters
  const cleaned = input.replace(/[^0-9]/g, "").slice(0, 12);

  // Mask first 8 digits with *
  const masked = cleaned
    .split("")
    .map((char, index) => (index < 8 ? "*" : char))
    .join("");

  // Add space after every 4 characters
  const formatted = masked.replace(/(.{4})/g, "$1 ").trim();

  return formatted;
}

export function maskPanNumber(pan: string) {
  const cleaned = pan
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);

  if (cleaned.length < 10) return cleaned; // Return as-is if incomplete

  const masked =
    "*****" + // First 5 characters masked
    cleaned.slice(5, 9) + // Show 4 digits
    "*"; // Last character masked

  return masked;
}

export function maskMobileNumber(mobile: string) {
  const cleaned = mobile
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);

  if (cleaned.length < 10) return cleaned; // Return as-is if incomplete

  const masked =
    "******" + // First 5 characters masked
    cleaned.slice(6, 10); // Show 4 digits

  return masked;
}

export const maskEmail = (email: string) => {
  if (!email) return "";

  const [localPart, domain] = email.split("@");

  if (!localPart || !domain) return email;

  // If local part is very short
  if (localPart.length <= 2) {
    return `${localPart[0]}*@${domain}`;
  }

  const firstChar = localPart[0];
  const lastChar = localPart[localPart.length - 1];
  const maskedPart = "*".repeat(localPart.length - 2);

  return `${firstChar}${maskedPart}${lastChar}@${domain}`;
};

export const states = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttarakhand",
  "Uttar Pradesh",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli",
  "Daman and Diu",
  "Delhi",
  "Lakshadweep",
  "Puducherry",
];
